import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { pillActive, pillInactive } from '../styles';
import { useOrderedSubtabs } from '../subtabs';
import { fetchMarketplaceMeta } from './api';
import CategoryTypesPanel from './CategoryTypesPanel';
import ProductCategoriesPanel from './ProductCategoriesPanel';
import ProductsPanel from './ProductsPanel';
import { MARKETPLACE_SUBTAB_CAPS, MarketplaceMeta } from './types';

type SubtabKey = 'types' | 'categories' | 'products';

const SUBTABS: { key: SubtabKey; label: string; cap: string }[] = [
  { key: 'types', label: 'Category Types', cap: MARKETPLACE_SUBTAB_CAPS.types },
  { key: 'categories', label: 'Product Categories', cap: MARKETPLACE_SUBTAB_CAPS.categories },
  { key: 'products', label: 'Products', cap: MARKETPLACE_SUBTAB_CAPS.products },
];

const AdminMarketplaceTab: React.FC = () => {
  const declared = useMemo(() => SUBTABS, []);
  const visibleSubtabs = useOrderedSubtabs('marketplace', declared);
  const [active, setActive] = useState<SubtabKey | null>(null);
  const [meta, setMeta] = useState<MarketplaceMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setActive((current) => {
      if (current && visibleSubtabs.some((t) => t.key === current)) return current;
      return (visibleSubtabs[0]?.key as SubtabKey | undefined) ?? null;
    });
  }, [visibleSubtabs]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchMarketplaceMeta();
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load marketplace admin data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (visibleSubtabs.length === 0) {
    return (
      <div className="text-sm text-[#8A8A8A]">
        You do not have access to any marketplace tools in this panel.
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {visibleSubtabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key)}
            className={`h-8 px-3 rounded-full text-[13px] font-medium transition-colors ${
              active === tab.key ? pillActive : pillInactive
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {!loading && !error && meta && active === 'types' && <CategoryTypesPanel />}
      {!loading && !error && meta && active === 'categories' && (
        <ProductCategoriesPanel meta={meta} />
      )}
      {!loading && !error && meta && active === 'products' && <ProductsPanel meta={meta} />}
    </div>
  );
};

export default AdminMarketplaceTab;
